import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from "@angular/material";
import { Inject } from "@angular/core";
import * as _ from 'lodash';
import { ApiService } from "../../../../services/api.service";
import { AlertService, MessageSeverity } from "../../../../services/alert.service";

/** How a single setting is edited, decided from the value already stored. */
type FieldKind = 'text' | 'multiline' | 'number' | 'boolean' | 'color' | 'json' | 'jsonText';

interface DefaultsField {
    key: string;
    kind: FieldKind;
    /** Bound to the input. Turned back into the stored type on save. */
    value: any;
}

/**
 * Editor for a benefit's default settings -- the values every site starts from, which
 * before this could only be changed in the SQL tables.
 *
 * One input per setting rather than a JSON textarea: the blob is what every site is built
 * from, and a stray comma in it takes the benefit out on all of them. The kind of input is
 * taken from the value already stored, so a number stays a number and the iconStyle blob
 * stays a string holding JSON -- the portal parses that one itself.
 *
 * featureName is read-only once a benefit exists. The portal looks features up by name
 * (getSettingByFeatureName('sitecontact'), ('ssogengold'), and others in its
 * whitelabel.service.ts), so renaming one here would quietly break those lookups.
 */
@Component({
    selector: 'edit-default-sheet',
    template: require('./edit-default-sheet.html'),
    styles: [require('./edit-default-sheet.css')],
})
export class EditDefaultSheetComponent implements OnInit {

    whitelabelFeature: any;

    creating = false;
    featureName = '';
    description = '';
    fields: DefaultsField[] = [];

    newFieldKey = '';
    saving = false;

    /** Filled in when a field will not convert back to the type it is stored as. */
    errors: string[] = [];

    constructor(private changeRef: ChangeDetectorRef,
                private alertService: AlertService,
                private apiService: ApiService,
                @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
                private bottomSheetRef: MatBottomSheetRef<EditDefaultSheetComponent>) {
        this.whitelabelFeature = data.whitelabelFeature || {};
    }

    ngOnInit() {
        this.creating = !this.whitelabelFeature.whitelabelFeatureId;
        this.featureName = this.whitelabelFeature.featureName || '';
        this.description = this.whitelabelFeature.description || '';
        this.fields = this.buildFields(this.storedSettings());
    }

    private storedSettings(): any {
        if (this.whitelabelFeature.defaultSettingsObj) {
            return this.whitelabelFeature.defaultSettingsObj;
        }

        try {
            return JSON.parse(this.whitelabelFeature.defaultSettings) || {};
        } catch (e) {
            return {};
        }
    }

    /** Key order is left as stored so the form matches what an admin saw in SQL. */
    private buildFields(settings: any): DefaultsField[] {
        return _.map(_.keys(settings), (key: string) => {
            const value = settings[key];
            const kind = this.kindOf(key, value);
            return { key: key, kind: kind, value: this.toInput(value, kind) };
        });
    }

    private kindOf(key: string, value: any): FieldKind {
        if (typeof value === 'boolean') { return 'boolean'; }
        if (typeof value === 'number') { return 'number'; }
        if (value !== null && typeof value === 'object') { return 'json'; }

        const text = value === null || value === undefined ? '' : String(value);

        // headingColor and its like get a picker rather than a box to type six hex digits
        // into. Caught by name, and by value for a setting named something else entirely.
        if (/colou?r$/i.test(key || '') || /^#[0-9a-f]{3,8}$/i.test(text.trim())) {
            return 'color';
        }

        // iconStyle and its like are stored as a string holding JSON, and the portal parses
        // them itself -- so they have to go back as a string, not as an object.
        const trimmed = text.trim();
        if (trimmed.length > 1 && (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[')) {
            return 'jsonText';
        }

        if (text.length > 60 || text.indexOf('\n') > -1) { return 'multiline'; }
        return 'text';
    }

    /**
     * What the swatch shows. A native colour input only understands #rrggbb, so a setting
     * holding a name, an rgba() or nothing at all falls back to black there -- the text box
     * beside it is what actually holds the value, and is left untouched until the swatch is
     * used.
     */
    colorSwatch(value: any): string {
        const text = String(value === null || value === undefined ? '' : value).trim();
        return /^#[0-9a-f]{6}$/i.test(text) ? text : '#000000';
    }

    /**
     * The rows to draw. A colour named after another setting -- headingColor beside heading
     * -- is drawn on that setting's row instead of one of its own, so the colour is where
     * the thing it colours is.
     */
    get visibleFields(): DefaultsField[] {
        return _.filter(this.fields, (f: DefaultsField) => !this.colorsAnotherField(f));
    }

    private colorsAnotherField(field: DefaultsField): boolean {
        const base = this.baseKeyOf(field.key);
        return !!base && _.some(this.fields, (f: DefaultsField) => f.key === base);
    }

    private baseKeyOf(key: string): string {
        const match = /^(.+?)(Color|Colour)$/.exec(key || '');
        return match ? match[1] : '';
    }

    /** The colour setting belonging to this row, once there is one. */
    colorOf(field: DefaultsField): DefaultsField {
        return _.find(this.fields, (f: DefaultsField) => this.baseKeyOf(f.key) === field.key);
    }

    /**
     * Which rows offer a colour. heading always does, so the picker is there to be found
     * before headingColor exists -- that setting is created the moment one is chosen. Any
     * other setting offers one once it has been given a colour.
     */
    offersColor(field: DefaultsField): boolean {
        return field.key === 'heading' || !!this.colorOf(field);
    }

    colorTextOf(field: DefaultsField): string {
        const companion = this.colorOf(field);
        return companion ? String(companion.value === null || companion.value === undefined ? '' : companion.value) : '';
    }

    /** Empties the box to drop the setting, so the heading goes back to the card's colour. */
    setColor(field: DefaultsField, value: string) {
        const text = (value || '').trim();
        let companion = this.colorOf(field);

        if (!text) {
            if (companion) { this.fields = _.without(this.fields, companion); }
            return;
        }

        if (!companion) {
            // Kept next to the setting it colours, so the saved JSON reads in that order.
            companion = { key: field.key + 'Color', kind: 'color', value: text };
            this.fields.splice(_.indexOf(this.fields, field) + 1, 0, companion);
            return;
        }

        companion.value = text;
    }

    private toInput(value: any, kind: FieldKind): any {
        if (kind === 'json') { return JSON.stringify(value, null, 2); }
        if (kind === 'boolean') { return !!value; }
        if (kind === 'number') { return value; }
        return value === null || value === undefined ? '' : String(value);
    }

    addField() {
        const key = (this.newFieldKey || '').trim();

        if (!key) { return; }

        if (_.some(this.fields, (f: DefaultsField) => f.key === key)) {
            this.alertService.showMessage("", "There is already a " + key + " setting.", MessageSeverity.warn);
            return;
        }

        this.fields.push({ key: key, kind: this.kindOf(key, ''), value: '' });
        this.newFieldKey = '';
    }

    /**
     * Only drops the row from the form. Nothing leaves until Save, and the server removes
     * the setting from the sites that were following the default along with it.
     */
    removeField(field: DefaultsField) {
        // Its colour goes with it; left behind it would have nothing to colour.
        this.fields = _.without(this.fields, field, this.colorOf(field));
    }

    get canSave(): boolean {
        if (this.saving) { return false; }
        if (this.creating && !(this.featureName || '').trim()) { return false; }
        return true;
    }

    save() {
        this.errors = [];

        const settings = {};

        _.each(this.fields, (field: DefaultsField) => {
            const value = this.fromInput(field);
            if (value !== undefined) { settings[field.key] = value; }
        });

        if (this.errors.length) {
            this.alertService.showMessage("", this.errors.join(' '), MessageSeverity.warn);
            return;
        }

        this.saving = true;

        if (this.creating) {
            this.createFeature(settings);
        } else {
            this.saveDefaults(settings);
        }
    }

    /** Back to the type the setting is stored as, or undefined with an error recorded. */
    private fromInput(field: DefaultsField): any {
        if (field.kind === 'boolean') { return !!field.value; }

        if (field.kind === 'number') {
            const text = String(field.value === null || field.value === undefined ? '' : field.value).trim();
            const parsed = Number(text);

            if (!text.length || isNaN(parsed)) {
                this.errors.push(field.key + " has to be a number.");
                return undefined;
            }

            return parsed;
        }

        const text = field.value === null || field.value === undefined ? '' : String(field.value);

        if (field.kind === 'json' || field.kind === 'jsonText') {
            try {
                const parsed = JSON.parse(text);
                // A jsonText setting is stored as the string, because that is what the portal
                // expects to parse; a json one is stored as the object it already was.
                return field.kind === 'json' ? parsed : text;
            } catch (e) {
                this.errors.push(field.key + " is not valid JSON.");
                return undefined;
            }
        }

        return text;
    }

    private createFeature(settings: any) {
        const feature = {
            featureName: (this.featureName || '').trim(),
            description: (this.description || '').trim(),
            defaultSettings: JSON.stringify(settings)
        };

        this.apiService.createWhitelabelFeature(feature).subscribe((created: any) => {
            this.saving = false;
            this.alertService.showMessage("", "Added " + feature.featureName + ".", MessageSeverity.success);
            this.bottomSheetRef.dismiss(created || true);
        }, error => {
            this.saving = false;
            this.alertService.showMessage("", this.messageFrom(error, "The benefit could not be added."),
                MessageSeverity.error);
            this.changeRef.detectChanges();
        });
    }

    private saveDefaults(settings: any) {
        this.apiService.saveFeatureDefaults(
            this.whitelabelFeature.whitelabelFeatureId, this.description, settings).subscribe((r: any) => {

            this.saving = false;
            this.alertService.showMessage("", this.savedMessage(r), MessageSeverity.success);
            this.bottomSheetRef.dismiss(true);
        }, error => {
            this.saving = false;
            this.alertService.showMessage("", this.messageFrom(error, "The defaults could not be saved."),
                MessageSeverity.error);
            this.changeRef.detectChanges();
        });
    }

    /**
     * Says what travelled. A site holding its own value for a setting keeps it, so an admin
     * who changes a default and sees no change on that site is told why here.
     */
    private savedMessage(result: any): string {
        const changed = (result && result.changedFields) || [];

        if (!changed.length) { return "Nothing had changed."; }

        let message = "Saved " + changed.length + (changed.length === 1 ? " change" : " changes") + ".";

        const synced = (result && result.syncedSites) || 0;
        if (synced) {
            message += " Applied to " + synced + (synced === 1 ? " site" : " sites") + " that followed the default.";
        }

        const kept = (result && result.keptCustom) || [];
        if (kept.length) {
            message += " Left alone where a site has its own value: " +
                _.map(kept, (k: any) => k.field + " (" + k.sites + ")").join(', ') + ".";
        }

        return message;
    }

    /**
     * The server explains its refusals in plain text -- "A benefit named x already exists."
     * -- so those are worth showing. HttpClient tries to parse the body as JSON and, when
     * that fails, hands back the raw text alongside the parse error, hence the second case.
     */
    private messageFrom(error: any, fallback: string): string {
        const body = error && error.error;

        if (typeof body === 'string' && body.length) { return body; }
        if (body && typeof body.text === 'string' && body.text.length) { return body.text; }

        return fallback;
    }

    cancel() {
        this.bottomSheetRef.dismiss();
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/components/whitelabels/whitelabel-configs/edit-default/edit-default-sheet.ts